/** Live quality KPI computation from hospital operational data. */

function computeReadmissionRate(allAdmissions) {
  const byPatient = {};
  (allAdmissions || []).forEach((a) => {
    if (!a.patientId || !a.admittedAt) return;
    if (!byPatient[a.patientId]) byPatient[a.patientId] = [];
    byPatient[a.patientId].push(a);
  });
  let readmits = 0;
  let discharges = 0;
  Object.values(byPatient).forEach((list) => {
    const sorted = [...list].sort((a, b) => new Date(a.admittedAt) - new Date(b.admittedAt));
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      if (!prev.dischargedAt) continue;
      discharges += 1;
      const gap = new Date(curr.admittedAt) - new Date(prev.dischargedAt);
      if (gap > 0 && gap <= 30 * 86400000) readmits += 1;
    }
  });
  return discharges > 0 ? Number(((readmits / discharges) * 100).toFixed(1)) : null;
}

function computeHandHygienePct(observations) {
  const totalOpp = (observations || []).reduce((s, o) => s + Number(o.opportunities || 0), 0);
  const totalComp = (observations || []).reduce((s, o) => s + Number(o.compliant || 0), 0);
  return totalOpp > 0 ? Math.round((totalComp / totalOpp) * 100) : null;
}

export function buildKQIs({
  beds = [],
  bedOccupancy = null,
  admissions = [],
  allAdmissions = [],
  incidents = [],
  feedbacks = [],
  labOrders = [],
  neonates = [],
  handHygieneObs = [],
}) {
  const totalBeds = bedOccupancy?.total ?? beds.length;
  const occupied = bedOccupancy?.occupied ?? beds.filter((b) => b.status === 'occupied' || b.patientId || b.admissionId).length;
  const occupancyPct = totalBeds ? Math.round((occupied / totalBeds) * 100) : null;

  const activeAdmissions = admissions.length ? admissions : (allAdmissions || []).filter((a) => a.status !== 'discharged');
  const now = Date.now();
  const losDays = activeAdmissions
    .filter((a) => a.admittedAt)
    .map((a) => (now - new Date(a.admittedAt).getTime()) / 86400000);
  const avgLos = losDays.length ? (losDays.reduce((s, d) => s + d, 0) / losDays.length).toFixed(1) : null;

  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const monthIncidents = incidents.filter((i) => new Date(i.date || i.createdAt) >= monthStart);
  const medErrors = monthIncidents.filter((i) => i.category === 'Medication Error').length;
  const falls = monthIncidents.filter((i) => i.category === 'Patient Fall').length;
  const hai = monthIncidents.filter((i) => i.category === 'Healthcare-Associated Infection').length;
  const sentinel = monthIncidents.filter((i) => i.severity === 'Sentinel').length;

  const patientDays = Math.max(activeAdmissions.length * 30, 1);
  const fallRate = ((falls / patientDays) * 1000).toFixed(1);
  const medErrorPct = activeAdmissions.length ? ((medErrors / activeAdmissions.length) * 100).toFixed(1) : '0.0';

  const ratedFeedback = feedbacks.filter((f) => f.ratings?.['Overall Experience'] != null);
  const avgSatisfaction = ratedFeedback.length
    ? (ratedFeedback.reduce((s, f) => s + Number(f.ratings['Overall Experience']), 0) / ratedFeedback.length).toFixed(1)
    : null;

  const verifiedLabs = labOrders.filter((o) => o.verifiedAt && o.createdAt);
  const tatMinutes = verifiedLabs.map((o) => (new Date(o.verifiedAt) - new Date(o.createdAt)) / 60000);
  const avgLabTat = tatMinutes.length
    ? Math.round(tatMinutes.reduce((s, t) => s + t, 0) / tatMinutes.length)
    : null;
  const labTatPct = verifiedLabs.length
    ? Math.round((tatMinutes.filter((t) => t < 120).length / verifiedLabs.length) * 100)
    : null;

  const monthNeonates = neonates.filter((n) => {
    const d = n.dob || n.createdAt;
    return d && new Date(d) >= monthStart;
  });
  const csCount = monthNeonates.filter((n) => /lscs|caesarean|c-?section/i.test(String(n.deliveryType || ''))).length;
  const csRate = monthNeonates.length ? Math.round((csCount / monthNeonates.length) * 100) : null;

  const readmissionRate = computeReadmissionRate(allAdmissions.length ? allAdmissions : admissions);
  const handHygienePct = computeHandHygienePct(handHygieneObs);

  return [
    {
      indicator: 'Bed Occupancy Rate',
      value: occupancyPct != null ? `${occupancyPct}%` : '—',
      target: '≥70%',
      status: occupancyPct == null ? 'na' : occupancyPct >= 70 ? 'ok' : 'warn',
      dept: 'Administration',
      frequency: 'Daily',
      live: occupancyPct != null,
      icon: '🛏',
    },
    {
      indicator: 'Current IP Length of Stay (avg)',
      value: avgLos != null ? `${avgLos} days` : '—',
      target: '≤5 days',
      status: avgLos == null ? 'na' : Number(avgLos) <= 5 ? 'ok' : 'warn',
      dept: 'Medical',
      frequency: 'Live',
      live: avgLos != null,
      icon: '📅',
    },
    {
      indicator: 'Readmission Rate (30 days)',
      value: readmissionRate != null ? `${readmissionRate}%` : '—',
      target: '<5%',
      status: readmissionRate == null ? 'na' : readmissionRate < 5 ? 'ok' : 'warn',
      dept: 'Medical',
      frequency: 'Monthly',
      live: readmissionRate != null,
      icon: '🔄',
    },
    {
      indicator: 'Medication Errors (this month)',
      value: String(medErrors),
      target: '<1 per 100 admissions',
      status: medErrors === 0 ? 'ok' : medErrors <= 2 ? 'warn' : 'bad',
      dept: 'Pharmacy/Nursing',
      frequency: 'Monthly',
      live: true,
      icon: '💊',
    },
    {
      indicator: 'Patient Falls (this month)',
      value: String(falls),
      target: '<3 per 1000 pt-days',
      status: falls === 0 ? 'ok' : Number(fallRate) < 3 ? 'ok' : 'warn',
      dept: 'Nursing',
      frequency: 'Monthly',
      live: true,
      icon: '⚠',
    },
    {
      indicator: 'HAI Reports (this month)',
      value: String(hai),
      target: '0',
      status: hai === 0 ? 'ok' : 'warn',
      dept: 'HIC',
      frequency: 'Monthly',
      live: true,
      icon: '🔬',
    },
    {
      indicator: 'Hand Hygiene Compliance',
      value: handHygienePct != null ? `${handHygienePct}%` : '—',
      target: '≥90%',
      status: handHygienePct == null ? 'na' : handHygienePct >= 90 ? 'ok' : 'warn',
      dept: 'Infection Control',
      frequency: 'Monthly',
      live: handHygienePct != null,
      icon: '🤲',
    },
    {
      indicator: 'Patient Satisfaction Score',
      value: avgSatisfaction != null ? `${avgSatisfaction}/5` : '—',
      target: '≥4.0/5',
      status: avgSatisfaction == null ? 'na' : Number(avgSatisfaction) >= 4 ? 'ok' : 'warn',
      dept: 'All',
      frequency: 'Monthly',
      live: avgSatisfaction != null,
      icon: '⭐',
    },
    {
      indicator: 'Lab Turnaround Time (avg)',
      value: avgLabTat != null ? `${avgLabTat} min` : '—',
      target: '<60 min',
      status: avgLabTat == null ? 'na' : avgLabTat < 60 ? 'ok' : 'warn',
      dept: 'Lab',
      frequency: 'Weekly',
      live: avgLabTat != null,
      icon: '🧪',
    },
    {
      indicator: 'Lab TAT within 2 hrs',
      value: labTatPct != null ? `${labTatPct}%` : '—',
      target: '>90%',
      status: labTatPct == null ? 'na' : labTatPct >= 90 ? 'ok' : 'warn',
      dept: 'Lab',
      frequency: 'Weekly',
      live: labTatPct != null,
      icon: '🧪',
    },
    {
      indicator: 'C-section Rate (NICU births, month)',
      value: csRate != null ? `${csRate}%` : '—',
      target: '<30%',
      status: csRate == null ? 'na' : csRate < 30 ? 'ok' : 'warn',
      dept: 'Obstetrics',
      frequency: 'Monthly',
      live: csRate != null,
      icon: '👶',
    },
    {
      indicator: 'Open CAPAs',
      value: String(incidents.filter((i) => i.status !== 'Closed').length),
      target: 'Close within 30 days',
      status: incidents.filter((i) => i.status !== 'Closed').length <= 3 ? 'ok' : 'warn',
      dept: 'Quality',
      frequency: 'Ongoing',
      live: true,
      icon: '📋',
    },
  ];
}
